import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { apiError } from "@/lib/errors";
import { PermissionMatrix, WidgetPicker, useAdminData } from "./adminComponents";
import DashboardGrid from "@/components/DashboardGrid";
import AddWidgetPicker from "@/components/AddWidgetPicker";
import { SectionHeader } from "@/components/SectionHeader";
import {
  layoutFromLegacy,
  addWidget as addToLayout,
  removeWidget as removeFromLayout,
  setSize as setLayoutSize,
  layoutsEqual,
} from "@/lib/dashboardLayout";
import { Plus, RotateCcw, Save, X, Pencil } from "lucide-react";
import { TemporaryPasswordField, CredentialResultDialog } from "./passwordControls";

// ---------- ROLES LIST ----------
export default function AdminRolesPage() {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    api.get("/admin/roles").then((r) => setRows(r.data.results)).catch((e) => toast.error(apiError(e)));
  }, []);
  return (
    <div className="p-6" data-testid="admin-roles-page">
      {/* RC4 · SectionHeader adoption landmark. */}
      <SectionHeader testid="page-section-landmark" className="sr-only">Roles & Permissions</SectionHeader>
      <h1 className="font-display text-carbon text-[26px] mb-4">Roles & Permissions</h1>
      <div className="bg-white border border-coach-silver rounded-md overflow-hidden">
        <table className="w-full text-[13px]">
          <thead className="bg-road-white text-[10.5px] uppercase tracking-widest text-steel">
            <tr>
              <th className="text-left px-3 py-2">Role</th>
              <th className="text-left px-3 py-2">Description</th>
              <th className="text-left px-3 py-2 w-32">Users Assigned</th>
              <th className="text-left px-3 py-2 w-32">Category</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-coach-silver/60 hover:bg-road-white cursor-pointer"
                  data-testid={`admin-role-row-${r.key}`}
                  onClick={() => window.location.assign(`/administration/roles/${r.key}`)}>
                <td className="px-3 py-2">
                  <div className="text-carbon font-medium">{r.label}</div>
                  <div className="text-[10.5px] text-steel font-mono">{r.key}</div>
                </td>
                <td className="px-3 py-2 text-carbon text-[12.5px]">{r.description || "—"}</td>
                <td className="px-3 py-2 text-carbon tabular">{r.users_assigned}</td>
                <td className="px-3 py-2">
                  {r.key === "super_admin" && <span className="text-[10px] uppercase tracking-widest text-touring-lime border border-touring-lime rounded-sm px-1">Super Admin</span>}
                  {r.is_future_shop_role && <span className="text-[10px] uppercase tracking-widest text-amber-hold border border-amber-hold rounded-sm px-1">Future Shop</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------- ROLE DETAIL ----------
export function AdminRoleDetailPage() {
  const { key } = useParams();
  const [role, setRole] = useState(null);
  const [users, setUsers] = useState([]);
  const { permCatalog, rootOnlyKeys, widgets, me } = useAdminData();
  const actorPerms = useMemo(() => new Set(me?.allowed || []), [me]);
  const actorIsSuperAdmin = actorPerms.has("admin.super_admin");
  const locked = key === "super_admin" && !actorIsSuperAdmin;

  const load = async () => {
    try {
      const r = await api.get(`/admin/roles/${key}`).then((r) => r.data);
      setRole(r);
      const u = await api.get(`/admin/users?role_class_id=${r.id}`).then((r) => r.data.results);
      setUsers(u);
    } catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [key]);

  const save = async (patch) => {
    try {
      const r = await api.patch(`/admin/roles/${key}`, patch).then((r) => r.data);
      setRole({ ...r, users_assigned: role.users_assigned });
      toast.success("Role saved");
    } catch (e) { toast.error(apiError(e)); }
  };

  if (!role || !permCatalog) return <div className="p-6 text-steel">Loading…</div>;

  return (
    <div className="p-6 space-y-4" data-testid="admin-role-detail">
      <div className="text-[12px]"><Link to="/administration/roles" className="text-coach-blue hover:underline">← All roles</Link></div>
      <div className="flex items-baseline justify-between">
        <div>
          <h1 className="font-display text-carbon text-[24px]">{role.label}</h1>
          <div className="text-[11.5px] text-steel">
            <span className="font-mono">{role.key}</span>
            {role.perspective_label && <> · Perspective: <span className="text-carbon">{role.perspective_label}</span></>}
          </div>
        </div>
        {locked && <span className="text-[11px] text-amber-hold">Super Admin authorization required to edit this role.</span>}
      </div>
      <div className="bg-white border border-coach-silver rounded-md p-4">
        <div className="text-[10.5px] uppercase tracking-widest text-steel mb-1 font-medium">Description</div>
        <div className="text-[13px] text-carbon">{role.description || "—"}</div>
      </div>

      <PermissionsSection role={role} catalog={permCatalog} rootOnlyKeys={rootOnlyKeys} locked={locked}
                          actorIsSuperAdmin={actorIsSuperAdmin} onSave={save} />
      <DashboardSection role={role} catalog={widgets} locked={locked}
                        actorPerms={actorPerms} onSave={save} />

      <div className="bg-white border border-coach-silver rounded-md">
        <div className="px-3 py-2 border-b border-coach-silver text-[11px] uppercase tracking-widest text-steel font-medium bg-road-white">
          Assigned Users ({users.length})
        </div>
        <table className="w-full text-[12.5px]">
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-t border-coach-silver/60">
                <td className="px-3 py-1.5">
                  <Link to={`/administration/users/${u.id}`} className="text-coach-blue hover:underline">{u.name}</Link>
                </td>
                <td className="px-3 py-1.5 text-steel">{u.job_title || ""}</td>
                <td className="px-3 py-1.5 font-mono text-[11px] text-steel">{u.email}</td>
              </tr>
            ))}
            {users.length === 0 && <tr><td className="p-3 text-steel italic">No users assigned to this role.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PermissionsSection({ role, catalog, rootOnlyKeys, locked, actorIsSuperAdmin, onSave }) {
  const [draft, setDraft] = useState(role.default_permissions || {});
  useEffect(() => setDraft(role.default_permissions || {}), [role.id]);
  const adminAutoInherit = Boolean(role?.is_admin) && role.key !== "super_admin";
  return (
    <div>
      <div className="flex items-baseline justify-between mb-2">
        <h2 className="text-[16px] text-carbon font-medium">Default Permissions</h2>
        <button onClick={() => onSave({ default_permissions: draft })} disabled={locked || adminAutoInherit}
                data-testid="admin-role-save-permissions"
                className="h-9 px-4 rounded-sm text-[12.5px] font-semibold bg-coach-blue hover:bg-deep-road-navy text-white disabled:opacity-50">
          {adminAutoInherit ? "Auto-Inherited" : "Save Permissions"}
        </button>
      </div>
      <PermissionMatrix catalog={catalog} rootOnlyKeys={rootOnlyKeys} roleDefaults={draft} mode="role"
                        onChangeDefaults={setDraft}
                        superAdminLocked={!actorIsSuperAdmin}
                        adminAutoInherit={adminAutoInherit} />
    </div>
  );
}

function DashboardSection({ role, catalog, locked, actorPerms, onSave }) {
  // Phase C Wave 2B — Admin Role Default editor now reuses the shared
  // <DashboardGrid> in edit mode + <AddWidgetPicker>. The role's
  // `default_dashboard_config` may be legacy list[str] or rich layout
  // list[{widget_key, order, size}]; we normalize both on load and
  // always save the rich shape.
  const normalize = (raw) => {
    const arr = Array.isArray(raw) ? raw : [];
    return arr
      .map((it, i) =>
        typeof it === "string"
          ? { widget_key: it, order: i, size: "standard" }
          : { widget_key: it.widget_key, order: i, size: it.size || "standard" }
      )
      .filter((it) => it.widget_key);
  };

  const [savedLayout, setSavedLayout] = useState(() => normalize(role.default_dashboard_config));
  const [pending, setPending] = useState(() => normalize(role.default_dashboard_config));
  const [editMode, setEditMode] = useState(false);
  const [showPicker, setShowPicker] = useState(false);

  useEffect(() => {
    const n = normalize(role.default_dashboard_config);
    setSavedLayout(n);
    setPending(n);
    setEditMode(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role.id]);

  const active = editMode ? pending : savedLayout;
  const currentKeys = active.map((it) => it.widget_key);
  const dirty = editMode && !layoutsEqual(pending, savedLayout);

  const enterEdit = () => { setPending(savedLayout); setEditMode(true); };
  const cancel   = () => { setPending(savedLayout); setEditMode(false); };
  const save     = async () => {
    await onSave({ default_dashboard_config: pending });
    setSavedLayout(pending);
    setEditMode(false);
  };
  const restore  = () => setPending([]); // Wire form — an empty default is what "no defaults" means
  const emptyPayloads = useMemo(() => ({}), []);

  return (
    <div>
      <div className="flex items-baseline justify-between mb-2">
        <div>
          <h2 className="text-[16px] text-carbon font-medium">Default Dashboard</h2>
          <div className="text-[11.5px] text-steel">
            The layout new users of this role inherit unless they set a personal override.
          </div>
        </div>
        {!editMode ? (
          <button
            type="button"
            onClick={enterEdit}
            disabled={locked}
            data-testid="admin-role-dashboard-edit"
            className="inline-flex items-center gap-1 h-9 px-3 rounded-sm text-[12.5px] border border-coach-silver hover:border-coach-blue disabled:opacity-50"
          >
            <Pencil size={12} /> Edit Default
          </button>
        ) : (
          <div className="flex items-center gap-1.5" data-testid="admin-role-dashboard-toolbar">
            <button type="button" onClick={() => setShowPicker(true)}
                    data-testid="admin-role-dashboard-add"
                    className="inline-flex items-center gap-1 h-9 px-2 rounded-sm text-[12.5px] border border-coach-silver hover:border-coach-blue">
              <Plus size={12} /> Add Widget
            </button>
            <button type="button" onClick={restore}
                    data-testid="admin-role-dashboard-clear"
                    className="inline-flex items-center gap-1 h-9 px-2 rounded-sm text-[12.5px] border border-coach-silver hover:border-amber-hold">
              <RotateCcw size={12} /> Clear
            </button>
            <button type="button" onClick={cancel}
                    data-testid="admin-role-dashboard-cancel"
                    className="inline-flex items-center gap-1 h-9 px-2 rounded-sm text-[12.5px] border border-coach-silver hover:border-critical-red">
              <X size={12} /> Cancel
            </button>
            <button type="button" onClick={save} disabled={!dirty}
                    data-testid="admin-role-save-dashboard"
                    className="inline-flex items-center gap-1 h-9 px-3 rounded-sm text-[12.5px] font-semibold bg-coach-blue hover:bg-deep-road-navy disabled:opacity-50 text-white">
              <Save size={12} /> Save Role Default
            </button>
          </div>
        )}
      </div>

      {editMode && (
        <div className="mb-3 px-3 py-2 rounded-sm bg-amber-hold/10 border border-amber-hold/30 text-carbon text-[12px]">
          <strong>Editing role default.</strong> Add / remove / drag / resize widgets — this is what users of this role class inherit unless they set a personal override.
        </div>
      )}

      {active.length === 0 ? (
        <div className="bg-white border border-dashed border-coach-silver rounded-md p-6 text-center text-[12.5px] text-steel">
          No default widgets. Users in this role will see an empty dashboard until they add widgets themselves.
        </div>
      ) : (
        <DashboardGrid
          layout={active}
          payloads={emptyPayloads}
          editMode={editMode}
          onReorder={setPending}
          onResize={(k, s) => setPending((p) => setLayoutSize(p, k, s))}
          onRemove={(k) => setPending((p) => removeFromLayout(p, k))}
        />
      )}

      <AddWidgetPicker
        open={showPicker}
        onClose={() => setShowPicker(false)}
        onPick={(k) => setPending((p) => addToLayout(p, k))}
        currentKeys={currentKeys}
        allowedPermissions={[...(actorPerms || [])]}
      />
    </div>
  );
}

// ---------- NEW USER ----------
export function AdminUserNewPage() {
  const { roles, me } = useAdminData();
  const [draft, setDraft] = useState({
    first_name: "", last_name: "", job_title: "", department: "",
    role_class_id: "", profile_photo_url: "", email: "",
  });
  const [tempPw, setTempPw] = useState("");
  const [mustChange, setMustChange] = useState(true);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const actorPerms = useMemo(() => new Set(me?.allowed || []), [me]);
  const visibleRoles = actorPerms.has("admin.super_admin") ? roles : roles.filter((r) => r.key !== "super_admin");
  const create = async () => {
    setError(null);
    try {
      const body = {
        ...draft,
        email: draft.email.trim() || undefined,
        temporary_password: tempPw.trim() || undefined,
        must_change_password: mustChange,
      };
      const d = await api.post("/admin/users", body).then((r) => r.data);
      // Server-generated password (or the admin-supplied one) is returned
      // ONCE. Show it in a confirmation modal before we route away.
      setResult({ id: d.id, email: d.email, password: d.temporary_password });
    } catch (e) { setError(apiError(e)); toast.error(apiError(e)); }
  };
  return (
    <div className="p-6 max-w-2xl" data-testid="admin-user-new">
      <div className="mb-2 text-[12px]"><Link to="/administration/users" className="text-coach-blue hover:underline">← All users</Link></div>
      <h1 className="font-display text-carbon text-[24px] mb-4">New User</h1>
      <div className="bg-white border border-coach-silver rounded-md p-4 space-y-3">
        {["first_name","last_name","job_title","department","profile_photo_url"].map((k) => (
          <div key={k}>
            <label className="text-[11px] uppercase tracking-widest text-steel">{k.replace(/_/g," ")}</label>
            <input type="text" value={draft[k]}
                   onChange={(e) => setDraft({...draft, [k]: e.target.value})}
                   data-testid={`admin-new-${k}`}
                   className="mt-0.5 w-full border border-coach-silver rounded-sm text-[13px] px-2 py-1" />
          </div>
        ))}
        <div>
          <label className="text-[11px] uppercase tracking-widest text-steel">Login email</label>
          <input type="email" value={draft.email}
                 onChange={(e) => setDraft({...draft, email: e.target.value})}
                 data-testid="admin-new-email"
                 placeholder="Leave blank to auto-generate (firstname@allaccess.test)"
                 className="mt-0.5 w-full border border-coach-silver rounded-sm text-[13px] px-2 py-1" />
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-widest text-steel">Role class</label>
          <select value={draft.role_class_id} onChange={(e) => setDraft({...draft, role_class_id: e.target.value})}
                  data-testid="admin-new-role_class_id"
                  className="mt-0.5 w-full border border-coach-silver rounded-sm text-[13px] px-2 py-1">
            <option value="">— Select —</option>
            {visibleRoles.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
          </select>
        </div>
        <TemporaryPasswordField value={tempPw} onChange={setTempPw}
                                testidPrefix="admin-new-tempPw" />
        <label className="flex items-center gap-2 text-[12px] text-carbon">
          <input type="checkbox" checked={mustChange}
                 onChange={(e) => setMustChange(e.target.checked)}
                 data-testid="admin-new-must-change" />
          Require password change at next login
        </label>
        {error && <div className="text-[12px] text-critical-red">{error}</div>}
        <button onClick={create} data-testid="admin-new-submit"
                disabled={!draft.first_name || !draft.role_class_id}
                className="h-9 px-4 rounded-sm text-[12.5px] font-semibold bg-coach-blue hover:bg-deep-road-navy text-white disabled:opacity-50">
          Create User
        </button>
      </div>
      <CredentialResultDialog open={!!result} onClose={() => {
        const id = result?.id;
        setResult(null);
        if (id) window.location.assign(`/administration/users/${id}`);
      }} email={result?.email} password={result?.password} actionLabel="created" />
    </div>
  );
}
